import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Comparison,
  DataType,
  Rule,
  RuleInterface,
  WorkflowStep,
  WorkflowStepOutputParameterContainer,
} from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TaskDefinitionsService } from '../../../../data-services/task-definitions.service';
import { IfElseBranchWorkflowStepTaskDefinitionId } from '../../../component-loader/constants';
import { AutomationErrorHandlerService } from '../../../services';
import {
  OutputParameterSelectorComponent,
  SelectedValues,
} from '../../common/output-parameter-selector/output-parameter-selector.component';
import { buildStepReferenceString, extractWorkflowStepIdFromReplacementTag } from '../../common/utils';
import { FilterType, Subgroup } from '../definitions';
import { FilterDefinitionService } from '../definitions/filter-definition.service';
import { FilterDataSelectorComponent } from '../filter-data/filter-data-selector/filter-data-selector.component';

export interface FilterOptionControlValueInterface {
  taskDefinitionId: string;
  inputParameters: string;
  dataType: DataType;
  paramId: string;
}

@Component({
  selector: 'automata-workflow-step-rules-filter',
  templateUrl: './workflow-step-rules-filter.component.html',
  styleUrls: ['./workflow-step-rules-filter.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    GalaxyTooltipModule,
    OutputParameterSelectorComponent,
    MatButtonToggleModule,
    FilterDataSelectorComponent,
  ],
})
export class WorkflowStepRulesFilterComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChildren(FilterDataSelectorComponent) filterDataSelectorComponents!: QueryList<FilterDataSelectorComponent>;

  @Input() rule: RuleInterface;
  @Input() taskDefinitionId: string;
  @Input() showClose = false;
  @Input() displayInternalFilters = false;
  @Input() fallbackNextStep: string;
  @Input() canChangeTaskDefinition = true;
  @Input() predecessorOutputParams: WorkflowStepOutputParameterContainer[];
  @Input() currentWorkflowStepId: string;
  @Input() currentStepData: string;
  @Input() selectedTaskDefinitionId: string;
  @Input() isGoal = false;

  @Output() removedCondition = new EventEmitter();
  @Output() newTaskDefinitionId = new EventEmitter<string>();
  @Output() newData = new EventEmitter<{ [key: string]: string }>();

  AND = Comparison.And;
  OR = Comparison.Or;

  operator: Comparison = Comparison.And;
  filterValues: Rule[];

  destroyed$$ = new Subject<void>();

  filterOptionControl: FormControl<FilterOptionControlValueInterface> =
    new FormControl<FilterOptionControlValueInterface>(null);
  filterSelectOptions$: Observable<SelectInputOptionInterface[]>;

  filterSubgroupControl: UntypedFormControl = new UntypedFormControl('');
  filterSubgroupOptions$: Observable<SelectInputOptionInterface[]>;
  currentlySelectedSubgroup$$ = new BehaviorSubject<string>('');
  currentlySelectedSubgroup$ = this.currentlySelectedSubgroup$$.asObservable().pipe(shareReplay(1));

  currentTaskDefinitionId = '';
  ifElseBranchWorkflowStepTaskDefinitionId = IfElseBranchWorkflowStepTaskDefinitionId;

  // filterDataValue will hold the previously selected data for the step and param selections
  filterDataValue = '';

  /** Emits true when the form is loading, false when it is not. */
  private isLoading$$ = new BehaviorSubject(true);
  public isLoading$: Observable<boolean> = this.isLoading$$.asObservable();

  constructor(
    private taskDefinitionsService: TaskDefinitionsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private readonly errorHandler: AutomationErrorHandlerService,
    private readonly filterDefinitions: FilterDefinitionService,
  ) {}

  private resetFilters(): void {
    this.filterValues = [];
    this.cdr.detectChanges();
    this.filterValues.push(null);
    this.cdr.detectChanges();
  }

  private mapFilterToOption(
    initialTaskDefinitionId: string,
    filter: FilterType,
    valid: boolean,
    hasAccessToRun: boolean,
    subgroups: Subgroup[],
  ): SelectInputOptionInterface {
    let description = '';
    if (!hasAccessToRun) {
      description = this.translate.instant('AUTOMATIONS.EDITOR.NO_ACCESS_TO_RUN_SHORT');
    } else if (!valid) {
      description = this.translate.instant('AUTOMATIONS.EDITOR.MORE_INFO_NEEDED_SHORT');
    }

    const hasNoSubgroups =
      filter.taskDefinitionId === IfElseBranchWorkflowStepTaskDefinitionId && subgroups.length === 0;

    return {
      label: this.translate.instant(filter.titleTextKey),
      description: description,
      value: {
        taskDefinitionId: filter.taskDefinitionId,
        inputParameters: filter.inputParameters,
        // The data types of these params will be the same, the only reason inputParameters is a list and not a single value is if a step returns two of the
        // 'replaceable' input params
        dataType: filter.inputParameters?.[0]?.paramDataType,
        paramId: filter.inputParameters?.[0]?.paramId,
      },
      disabled: !(valid && hasAccessToRun) || hasNoSubgroups,
      selected: filter.taskDefinitionId === initialTaskDefinitionId,
    };
  }

  ngOnInit(): void {
    this.isLoading$$.next(true);
    this.taskDefinitionsService.setPrecedingStepIds(this.predecessorOutputParams?.map((op) => op.workflowStep.id));

    this.filterSelectOptions$ = combineLatest([
      this.taskDefinitionsService.filters$,
      this.taskDefinitionsService.internalFilters$,
      this.taskDefinitionsService.triggers$,
      this.taskDefinitionsService.internalTriggers$,
      this.taskDefinitionsService.currentTaskDefinitionId$$,
    ]).pipe(
      // Every time new filter data arrives be sure to update which are valid to display and use.
      takeUntil(this.destroyed$$),
      map(([filters, internalFilters, triggers, internalTriggers, selectedTaskDefinitionId]) => {
        const trigger = triggers.concat(internalTriggers).find((t) => t.taskDefinition.id === selectedTaskDefinitionId);
        return {
          allFilters: filters.concat(internalFilters),
          predecessorOutputParams: trigger
            ? [
                new WorkflowStepOutputParameterContainer({
                  workflowStep: new WorkflowStep({
                    taskDefinitionId: trigger?.taskDefinition.id,
                    data: '{}',
                    choices: trigger?.taskDefinition.choices,
                    id: 'trigger',
                    name: 'Trigger',
                  }),
                  outputParameters: trigger?.taskDefinition.outputParameters,
                }),
              ]
            : [],
        };
      }),
      switchMap(({ allFilters, predecessorOutputParams }) => {
        const allFilters$: Observable<SelectInputOptionInterface>[] = allFilters.map((f) => {
          /**
           * Usually When the filters is loaded from an if/else action, the predecessorOutputParams not will be empty.
           * So if it is empty then we know a trigger is being selected, then we will set the predecessorOutputParams to the current predecessorOutputParams
           */
          if (
            f.taskDefinition.id === IfElseBranchWorkflowStepTaskDefinitionId &&
            predecessorOutputParams.length &&
            !this.predecessorOutputParams?.length
          ) {
            this.predecessorOutputParams = predecessorOutputParams;
          }
          const filterType = this.filterDefinitions.getFilter(f.taskDefinition.id, this.predecessorOutputParams);
          if (!filterType) {
            return of(null);
          }
          const hasComponents$ = filterType.dataComponents$.pipe(map((dataComponents) => dataComponents.size > 0));
          const subgroups$ = filterType.subgroups$ || of([]);
          return combineLatest([hasComponents$, subgroups$]).pipe(
            map(([hasComponents, subgroups]) => {
              if (hasComponents && f.metadata.valid && f.metadata.hasAccessToRun) {
                const option = this.mapFilterToOption(
                  this.taskDefinitionId,
                  filterType,
                  f.metadata.valid,
                  f.metadata.hasAccessToRun,
                  subgroups,
                );
                return option;
              }
              return null;
            }),
            // if his.filterOptionControl has no value. Loop through the options and assign the first non-disabled one
          );
        });
        // WARNING: This is a forkJoin, which means it doesn't emit until all observables have completed. If there is a filter in
        // allFilters$ that doesn't complete (because there is no take(1) or similar) then this will never emit. This manifests as the Condition Type
        // dropdown in the if/else side panel shimmering/loading forever.
        return forkJoin(allFilters$);
      }),
      map((options) => (options || []).filter((o) => !!o)),
      tap((options) => {
        const selectedOption = options.find((o) => o.selected);
        if (selectedOption) {
          this.filterOptionControl.setValue(selectedOption.value);
          return;
        }
        const firstOption = options.find((o) => !o.disabled);
        if (firstOption) {
          this.filterOptionControl.setValue(firstOption.value);
        }
      }),
      // When the filters are done loading the panel can be saved.
      tap(() => this.isLoading$$.next(false)),
    );

    this.filterOptionControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        map((_) => {
          const data = JSON.parse(this.currentStepData || '{}');
          this.filterDataValue = data?.[this.filterOptionControl.value?.paramId];
        }),
      )
      .subscribe();

    this.filterSubgroupOptions$ = this.filterOptionControl.valueChanges.pipe(
      startWith(''),
      takeUntil(this.destroyed$$),
      switchMap((_) => {
        // check the current value of the filter option control, don't perform this operation if it's not a workflow step filter
        const workflowStepFilterExists =
          !!this.filterOptionControl.value &&
          this.filterOptionControl.value.taskDefinitionId === IfElseBranchWorkflowStepTaskDefinitionId;
        if (!workflowStepFilterExists) {
          return of(null);
        }
        // currently subgrouping is only supported with the WorkflowStep filter task definition
        const filterType = this.filterDefinitions.getFilter(
          IfElseBranchWorkflowStepTaskDefinitionId,
          this.predecessorOutputParams,
        );
        if (!filterType?.subgroups$) {
          return of(null);
        }

        // handles a previously saved workflow step rule
        let stepIdMatch = '';
        if (this.rule) {
          const ruleVariablePath = Rule.fromRuleInterface(this.rule).getVariablePath();
          stepIdMatch = extractWorkflowStepIdFromReplacementTag(ruleVariablePath);
        }

        return filterType.subgroups$.pipe(
          map((subgroups) => {
            const hasStepSaved = !!stepIdMatch;
            const options = subgroups.map((subgroup, i) => {
              const stepSelected = hasStepSaved && subgroup.id === stepIdMatch;
              if (hasStepSaved) {
                if (subgroup.id === stepIdMatch) {
                  // set the form control to have the step selected
                  this.filterSubgroupControl.setValue(subgroup.id);
                }
              } else if (this.currentWorkflowStepId) {
                // We need this to cover new branches being added
                if (this.filterSubgroupControl.value !== this.currentWorkflowStepId) {
                  this.filterSubgroupControl.setValue(this.currentWorkflowStepId);
                }
              } else {
                if (i === 0) {
                  // set the form control to have the first step selected
                  this.filterSubgroupControl.setValue(subgroup.id);
                }
              }
              return {
                label: subgroup.name,
                value: subgroup.id,
                selected: stepSelected,
              };
            });
            // If no options are selected, select the first one
            if (options.filter((o) => o.selected).length === 0) {
              options[0].selected = true;
              this.filterSubgroupControl.setValue(options[0].value);
            }
            return options;
          }),
        );
      }),
      // When the filters are done loading the panel can be saved.
      tap(() => this.isLoading$$.next(false)),
    );

    this.filterSubgroupControl.valueChanges.subscribe((value) => {
      if (value) {
        this.currentlySelectedSubgroup$$.next(value);
      }
    });
    this.filterOptionControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        tap((value) => {
          if (!!value && value.taskDefinitionId !== this.currentTaskDefinitionId) {
            this.currentTaskDefinitionId = value.taskDefinitionId;
            this.newTaskDefinitionId.emit(this.currentTaskDefinitionId);
            this.newData.emit({});
            this.resetFilters();
            this.cdr.detectChanges();
          }
        }),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const newTaskDefinitionId = changes?.taskDefinitionId?.currentValue;
    if (!!newTaskDefinitionId && newTaskDefinitionId !== this.currentTaskDefinitionId) {
      this.currentTaskDefinitionId = changes.taskDefinitionId.currentValue;
      this.newTaskDefinitionId.emit(this.currentTaskDefinitionId);
      this.initializeRules();
    }

    if (changes?.canChangeTaskDefinition?.currentValue === true) {
      this.filterOptionControl.enable();
    }
    if (changes?.canChangeTaskDefinition?.currentValue === false) {
      this.filterOptionControl.disable();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.unsubscribe();
  }

  initializeRules(): void {
    if (this.rule) {
      const andOr = Rule.fromRuleInterface(this.rule);
      if (andOr.comparison === Comparison.Or) {
        this.operator = Comparison.Or;
      }
      this.filterValues = andOr.value as Rule[];
    } else {
      this.filterValues = [null];
    }
  }

  outputParamSelected(selected: SelectedValues) {
    if (!!selected.param && !!selected.container) {
      this.newData.emit({
        [this.filterOptionControl.value.paramId]: buildStepReferenceString(
          selected.container.workflowStep.id,
          selected.param.settings.id,
        ),
      });
    }
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    let result = [];
    this.filterDataSelectorComponents.map((component) => {
      result = result.concat(...component.getControlsToValidateOnSave());
    });
    return result;
  }

  isFormValid(): boolean {
    let valid = !this.filterOptionControl.invalid;
    this.filterDataSelectorComponents.map((component) => {
      valid = valid && component.isFormValid();
    });
    return valid;
  }

  getRule(): RuleInterface {
    if (!this.filterDataSelectorComponents) {
      return;
    }

    const rules = this.filterDataSelectorComponents.map((component) => component.getValues());
    return {
      comparison: this.operator,
      value: JSON.stringify(rules),
      next: this.rule?.next || this.fallbackNextStep,
    };
  }

  addMore(): void {
    this.filterValues.push(null);
  }

  remove($event: any, index: number): void {
    this.filterValues.splice(index, 1);
  }

  removeFilter(): void {
    this.removedCondition.emit();
  }
}
